import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { X, Download } from "lucide-react";

export default function CohortDrillDrawer({ cohort, offset, onClose }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null); setErr("");
    api.get(`/admin/analytics/cohort/${cohort}/${offset}?page=${page}&page_size=50`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Could not load cohort users"));
  }, [cohort, offset, page]);

  const csvUrl = `${API}/admin/analytics/cohort/${cohort}/${offset}/export.csv`;
  const downloadCsv = async () => {
    try {
      const res = await api.get(`/admin/analytics/cohort/${cohort}/${offset}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cohort-${cohort}-m${offset}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { /* swallow */ }
  };

  const totalPages = data ? Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 50))) : 1;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="cohort-drill-drawer">
      <div className="w-full max-w-3xl bg-white shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex-1 min-w-0">
            <p className="overline">Cohort drill-down</p>
            <h2 className="font-display text-2xl mt-1" data-testid="cohort-drill-title">
              {cohort} · Month {offset}
            </h2>
            <p className="text-xs text-[var(--ff-text-muted)] mt-1">
              {data ? `${data.total} retained member${data.total === 1 ? "" : "s"}` : "Loading…"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadCsv} className="btn-secondary text-sm inline-flex items-center gap-1" data-testid="cohort-drill-csv" title={csvUrl}>
              <Download size={14}/> CSV
            </button>
            <button onClick={onClose} className="text-[var(--ff-text-muted)] hover:text-[var(--ff-text-primary)]" data-testid="cohort-drill-close" aria-label="Close">
              <X size={20}/>
            </button>
          </div>
        </div>

        <div className="p-6">
          {err && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800" data-testid="cohort-drill-error">{err}</div>}
          {!data && !err && <p className="text-[var(--ff-text-muted)]" data-testid="cohort-drill-loading">Loading cohort users…</p>}

          {data && data.rows.length === 0 && (
            <p className="text-sm text-[var(--ff-text-muted)] py-12 text-center" data-testid="cohort-drill-empty">
              No retained users in this bucket.
            </p>
          )}

          {data && data.rows.length > 0 && (
            <>
              <table className="w-full text-sm" data-testid="cohort-drill-table">
                <thead>
                  <tr className="text-left text-[var(--ff-text-muted)] border-b border-[var(--ff-border)]">
                    <th className="py-2 font-medium">Member</th>
                    <th className="py-2 font-medium">Signed up</th>
                    <th className="py-2 font-medium">Last order</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.user_id} className="border-b border-[var(--ff-border)] last:border-0" data-testid={`cohort-drill-row-${r.user_id}`}>
                      <td className="py-3">
                        <p className="font-medium">{r.name || "—"}</p>
                        <p className="text-xs text-[var(--ff-text-muted)]">{r.email}</p>
                      </td>
                      <td className="py-3 text-[var(--ff-text-secondary)] font-mono text-xs">{(r.signed_up_at || "").slice(0, 10)}</td>
                      <td className="py-3 text-[var(--ff-text-secondary)] font-mono text-xs">{(r.last_order_at || "").slice(0, 10) || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4 text-sm" data-testid="cohort-drill-pagination">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="btn-secondary text-sm disabled:opacity-40" data-testid="cohort-drill-prev">← Prev</button>
                  <span className="text-[var(--ff-text-muted)]">Page {page} of {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="btn-secondary text-sm disabled:opacity-40" data-testid="cohort-drill-next">Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
